import React, { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchData } from '../redux/actions/Data';
import { makeURL } from '../helperFunctions';
import useCheckAuthStateInterval from '../myHooks/useCheckAuthStateInterval';
import useIsCompany from '../myHooks/useIsCompany';
import useIsAdmin from '../myHooks/useIsAdmin';
import { subscriptionTypeNames } from '../config/subscriptionsConfig';

//pages
import SessionExpired from './Pages/SessionExpired';
import Home from './Pages/Home';

import BrandViewAll from './Pages/BrandPersonas/BrandViewAll';
import BrandDashboard from './Pages/BrandPersonas/BrandDashboard';

import BuyerViewAll from './Pages/BuyerPersonas/BuyerViewAll';
import BuyerDashboard from './Pages/BuyerPersonas/BuyerDashboard';

import ClientsViewAll from './Pages/Clients/ClientsViewAll';
import Admin from './Pages/Admin';

import CreateNew from './Pages/CreateNew';
import Consumer from './Pages/Consumer';
import Reports from './Pages/Reports';
import BrandConsumersViewAll from './Pages/BrandConsumers/BrandConsumersViewAll';
import BrandConsumersDashboard from './Pages/BrandConsumers/BrandConsumersDashboard';

import AppLoader from '../myComponents/loaders/appLoader';
import SocialMediaViewAll from './Pages/SocialMedia/SocialMediaViewAll';
import SocialMediaDashboard from './Pages/SocialMedia/SocialMediaDashboard';
import CompetitiveSetBrands from './Pages/CompetitiveSetBrands/CompetitiveSetBrandsViewAll';
import CompetitiveSetBrandsDashboard from './Pages/CompetitiveSetBrands/CompetitiveSetBrandsDashboard';
import ReportExportRoutes from './Pages/ReportExport';

const Routes = () => {
  const location = useLocation();
  const authState = useSelector(({ auth }) => auth);
  const dispatch = useDispatch();
  const isCompany = useIsCompany();
  const isAdmin = useIsAdmin();
  const isReportExport = location.pathname.startsWith(makeURL('/report-export'));

  //Settup authentication check interval
  const checkAuthInterval = isReportExport ? null : 1000 * 30; //30 seconds
  useCheckAuthStateInterval(dispatch, checkAuthInterval);

  useEffect(() => {
    if (!isReportExport && authState.user) {
      dispatch(fetchData(authState.user.customClaims.agencyId));
    }
  }, [authState.user, isReportExport]);

  if (isReportExport) {
    return <ReportExportRoutes />;
  }

  if (authState.initialAppLoadCheckingAuthState) {
    return <AppLoader />;
  }

  if (!authState.user) {
    return <SessionExpired />;
  }

  if (location.pathname === makeURL('') || location.pathname === makeURL('/')) {
    return <Redirect to={makeURL('/home')} />;
  }

  const restrictCompetitiveRouteAccess = authState?.user?.customClaims?.subscriptionType === subscriptionTypeNames.demo;
  const restrictYourBrandAndConsumers =
    authState?.user?.customClaims?.subscriptionType !== subscriptionTypeNames.enterprise_1000 &&
    authState?.user?.customClaims?.subscriptionType !== subscriptionTypeNames.enterprise_unlimited &&
    authState?.user?.customClaims?.subscriptionType !== subscriptionTypeNames.admin;

  return (
    <React.Fragment>
      <Switch>
        <Route path={makeURL('/home')} exact component={Home} />
        <Route path={makeURL('/create-new/:type/:id')} exact component={CreateNew} />

        {/* Brand personas */}
        <Route path={makeURL('/brand-personas/all')} exact component={BrandViewAll} />
        <Route path={makeURL('/brand-personas/:id')} exact component={BrandDashboard} />

        {/* Buyer personas */}
        <Route path={makeURL('/buyer-personas/all')} exact component={BuyerViewAll} />
        <Route path={makeURL('/buyer-personas/:id')} exact component={BuyerDashboard} />

        {/* Social Media */}
        <Route path={makeURL('/marketing-content/all')} exact component={SocialMediaViewAll} />
        <Route path={makeURL('/marketing-content/:id')} exact component={SocialMediaDashboard} />

        {/* Competitive Set Brands, demo account doesn't have access */}
        {!restrictCompetitiveRouteAccess && (
          <Route path={makeURL('/competitive-set-brands/all')} exact component={CompetitiveSetBrands} />
        )}
        {!restrictCompetitiveRouteAccess && (
          <Route path={makeURL('/competitive-set-brands/:id')} exact component={CompetitiveSetBrandsDashboard} />
        )}

        {/* Your brand and consumers, only enterpirse accounts have access */}
        {!restrictYourBrandAndConsumers && (
          <Route path={makeURL('/brand-consumers/all')} exact component={BrandConsumersViewAll} />
        )}
        {!restrictYourBrandAndConsumers && (
          <Route path={makeURL('/brand-consumers/:brandName/:batch')} exact component={BrandConsumersDashboard} />
        )}

        {/* Clients */}
        {isCompany && <Route path={makeURL('/clients/all')} exact component={ClientsViewAll} />}

        <Route path={makeURL('/consumer-dash')} exact component={Consumer} />

        <Route path={makeURL('/reports')} exact component={Reports} />

        {/* Admin */}
        {isAdmin && <Route path={makeURL('/all-mighty-admin')} exact component={Admin} />}

        <Redirect to={makeURL('/home')} />
      </Switch>
    </React.Fragment>
  );
};

export default Routes;
